import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  FiMail,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiBriefcase,
  FiArrowRight,
  FiClock,
} from "react-icons/fi";
import Header from "../Home/Header";
import Footers from "../Home/Footers";
import AxiosInstance from "../Home/AxiosInstance";
import "../Home/publicPages.css";
import "../Company/EmployerDetails.css";

const INITIAL_COLORS = [
  "#5b5fff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
];

const getInitialColor = (name) => {
  const code = (name || "A").charCodeAt(0) || 65;
  return INITIAL_COLORS[code % INITIAL_COLORS.length];
};

const getLogoUrl = (logo) => {
  if (!logo) return null;
  const path = String(logo).replace(/\\/g, "/");
  if (path.startsWith("http")) return path;
  return `${AxiosInstance.defaults.baseURL}/media/${path}`;
};

const formatWebsite = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://${url}`;
};

const formatAddress = (addr) => {
  if (!addr) return "";
  return [
    addr.address_line1,
    addr.address_line2,
    addr.city,
    addr.state?.state_name,
    addr.country?.country_name,
    addr.zip_code,
  ]
    .filter(Boolean)
    .join(", ");
};

export default function InstitutionDetails() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [institution, setInstitution] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchInstitution = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await AxiosInstance.get(
          `companies/company/detail/${companyId}/`
        );
        setInstitution(response.data);

        try {
          const addrRes = await AxiosInstance.get(
            `companies/company/addresses/${companyId}/`
          );
          if (Array.isArray(addrRes.data)) setAddresses(addrRes.data);
        } catch {
          setAddresses([]);
        }

        try {
          const [usersRes, batchesRes] = await Promise.all([
            AxiosInstance.get(`companies/company-users/${companyId}/`),
            AxiosInstance.get("companies/batches/"),
          ]);

          const companyUsers = usersRes.data?.company_users || [];
          const userIds = new Set(
            companyUsers.map((user) => Number(user.user_id)).filter(Boolean)
          );

          const batches = Array.isArray(batchesRes.data) ? batchesRes.data : [];
          const offered = batches.filter((batch) => {
            if (batch.is_published !== "Yes") return false;
            const uid =
              batch.course_material?.user?.user_id || batch.user?.user_id;
            return uid != null && userIds.has(Number(uid));
          });

          // Prefer unique course materials; keep earliest upcoming batch per course
          const byCourse = new Map();
          offered.forEach((batch) => {
            const materialId =
              batch.course_material?.course_material_id ||
              batch.course_material_id ||
              batch.batch_id;
            const existing = byCourse.get(materialId);
            if (!existing) {
              byCourse.set(materialId, batch);
              return;
            }
            const existingStart = new Date(existing.batch_starts_at || 0);
            const nextStart = new Date(batch.batch_starts_at || 0);
            if (nextStart > new Date() && nextStart < existingStart) {
              byCourse.set(materialId, batch);
            }
          });

          setCourses([...byCourse.values()]);
        } catch (courseErr) {
          console.error("Failed to load institute courses:", courseErr);
          setCourses([]);
        }
      } catch (err) {
        console.error("Failed to load institution:", err);
        setError(
          err.response?.status === 404
            ? "Institute not found."
            : "Failed to load institute details."
        );
      } finally {
        setLoading(false);
      }
    };

    if (companyId) fetchInstitution();
  }, [companyId]);

  if (loading) {
    return (
      <div className="EmployerPage min-vh-100 d-flex flex-column">
        <Header />
        <p className="emp-loading">Loading institute…</p>
        <Footers />
      </div>
    );
  }

  if (error || !institution) {
    return (
      <div className="EmployerPage min-vh-100 d-flex flex-column">
        <Header />
        <div className="emp-loading">
          <p className="text-danger">{error || "Institute not found."}</p>
          <nav className="als-breadcrumb als-breadcrumb-light" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="als-breadcrumb-sep" aria-hidden="true">
              ›
            </span>
            <Link to="/institutions">Institutes</Link>
          </nav>
        </div>
        <Footers />
      </div>
    );
  }

  const name = institution.company_name || "Institute";
  const logoUrl = getLogoUrl(institution.company_logo);
  const accent = getInitialColor(name);
  const website = formatWebsite(institution.website);
  const businessType =
    institution.business_type?.business_type || "Training Institute";
  const primaryAddress = addresses[0];

  return (
    <div className="EmployerPage min-vh-100 d-flex flex-column">
      <Header />

      <section className="emp-hero">
        <div className="emp-hero-inner">
          <div className="emp-hero-copy">
            <nav className="als-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <Link to="/institutions">Institutes</Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <span aria-current="page">{name}</span>
            </nav>
            <div className="emp-badges">
              <span className="emp-badge type">Institute</span>
              <span className="emp-badge mode">{businessType}</span>
            </div>
            <h1>{name}</h1>
            {institution.email && (
              <p className="emp-hero-email">{institution.email}</p>
            )}
            <div className="emp-meta">
              {primaryAddress?.city && (
                <div className="emp-meta-item">
                  <FiMapPin /> {primaryAddress.city}
                  {primaryAddress.state?.state_name
                    ? `, ${primaryAddress.state.state_name}`
                    : ""}
                </div>
              )}
              {institution.phone_no_1 && (
                <div className="emp-meta-item">
                  <FiPhone /> {institution.phone_no_1}
                </div>
              )}
              {website && (
                <div className="emp-meta-item">
                  <FiGlobe /> Website available
                </div>
              )}
            </div>
          </div>
          <div className="emp-hero-media">
            <div
              className="emp-hero-logo"
              style={
                !logoUrl
                  ? { background: `${accent}1a`, color: accent }
                  : undefined
              }
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                name.charAt(0).toUpperCase()
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="emp-body flex-grow-1">
        <div className="emp-layout">
          <main className="emp-main">
            <section className="emp-card">
              <h2>About the institute</h2>
              <p className="emp-prose">
                {name} partners with Open Learnings to deliver
                professional training, certifications, and industry-aligned
                learning programs.
              </p>
              <div className="emp-highlights">
                {courses.length > 0 && (
                  <span>
                    {courses.length} course{courses.length === 1 ? "" : "s"}
                  </span>
                )}
                <span>Certification</span>
                <span>Placement Support</span>
              </div>
            </section>

            {addresses.length > 0 && (
              <section className="emp-card">
                <h2>Locations</h2>
                <ul className="emp-list">
                  {addresses.map((addr) => (
                    <li key={addr.address_id}>
                      <FiMapPin className="emp-list-icon" />
                      <div>
                        {addr.address_type?.address_type && (
                          <strong>{addr.address_type.address_type}</strong>
                        )}
                        <p>{formatAddress(addr)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="emp-card">
              <h2>Courses offered</h2>
              {courses.length > 0 ? (
                <ul className="emp-internship-list">
                  {courses.map((batch) => {
                    const courseName =
                      batch.course_material?.course?.course_name ||
                      batch.batch_name ||
                      "Course";
                    const mode = batch.mode;
                    const duration = batch.duration;
                    const trainer = batch.course_material?.trainer;
                    const trainerName = trainer
                      ? trainer.alias_name ||
                        `${trainer.first_name || ""} ${
                          trainer.last_name || ""
                        }`.trim()
                      : "";

                    return (
                      <li key={batch.batch_id}>
                        <div>
                          <strong>{courseName}</strong>
                          <p>
                            {[mode, duration, trainerName]
                              .filter(Boolean)
                              .join(" · ") || "Details available on course page"}
                          </p>
                          {batch.batch_starts_at && (
                            <p>
                              <FiClock
                                style={{
                                  display: "inline",
                                  marginRight: 4,
                                  verticalAlign: "middle",
                                }}
                              />
                              Starts{" "}
                              {new Date(
                                batch.batch_starts_at
                              ).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="emp-view-role"
                          onClick={() => {
                            if (batch.course_material?.course_material_id) {
                              localStorage.setItem(
                                "course_material_id",
                                batch.course_material.course_material_id
                              );
                            }
                            navigate(`/courses/${batch.batch_id}`);
                          }}
                        >
                          View <FiArrowRight />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="emp-prose">
                  No published courses listed for this institute yet.
                </p>
              )}
            </section>
          </main>

          <aside className="emp-sidebar">
            <section className="emp-card emp-contact-card">
              <h2>Contact</h2>
              <ul className="emp-contact-list">
                {institution.email && (
                  <li>
                    <FiMail />
                    <a href={`mailto:${institution.email}`}>
                      {institution.email}
                    </a>
                  </li>
                )}
                {institution.phone_no_1 && (
                  <li>
                    <FiPhone />
                    <a href={`tel:${institution.phone_no_1}`}>
                      {institution.phone_no_1}
                    </a>
                  </li>
                )}
                {institution.phone_no_2 && (
                  <li>
                    <FiPhone />
                    <a href={`tel:${institution.phone_no_2}`}>
                      {institution.phone_no_2}
                    </a>
                  </li>
                )}
                {website && (
                  <li>
                    <FiGlobe />
                    <a href={website} target="_blank" rel="noreferrer">
                      Visit website
                    </a>
                  </li>
                )}
                <li>
                  <FiBriefcase />
                  <span>{businessType}</span>
                </li>
              </ul>
              <button
                type="button"
                className="emp-cta"
                onClick={() => navigate("/courses")}
              >
                Browse courses
              </button>
            </section>
          </aside>
        </div>
      </div>

      <Footers />
    </div>
  );
}
